import dynamic from "@redux-dynostore/react-redux";
import subspaced from "@redux-dynostore/react-redux-subspace";
import { attachReducer } from "@redux-dynostore/redux-subspace";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { connect } from "react-redux";

import styled, { withTheme } from "styled-components";

import SmartFoodDrinkSelector from "../FoodDrinkSelector/FoodDrinkSelector";
import SmartDietDiaryCharts from "../Charts/DietDiaryCharts";
import CustomTabs, {
  CustomTabContent,
  TabButton,
} from "../../ui-library/CustomTabs/CustomTabs";

import NutritionTable from "../../library/NutritionTable/NutritionTable";
import FrequentlyForgottenItems from "../../library/FrequentlyForgottenItems/FrequentlyForgottenItems";
import { sendNotification } from "../../ToastManager/ToastManager";
import { ToastType } from "../../ToastManager/constants";

import AnimatedButton from "../../ui-library/Buttons/AnimatedButton/AnimatedButton";
import SecondaryButton from "../../ui-library/Buttons/SecondaryButton/SecondaryButton";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";

import {
  favoriteMealReducer,
  mapDispatchToProps,
  mapStateToProps,
} from "./FavoriteMeal.store";
import Favorite from "../../ui-library/Favorite/Favorite";
import DailyTargets from "../../library/Goals/DailyTargets";
import LabelledInput from "../../ui-library/LabelledInput/LabelledInput";
import Checkbox from "../../ui-library/Checkbox/Checkbox";
import { scrollModalToTop } from "../../../modules/utils";
import {
  dailyTargetIds,
  getMacronutrientColor,
  PIE_CHART_SERIES_ATTRS,
} from "../../../modules/constants";
import { GoalLevels } from "../../library/Goals/Constants";
import {
  DietDairyTypes,
  dietDairyTypeToString,
} from "../DietDiary/DietDiary.constants";
import { useTranslation } from "react-i18next";
import {
  addOtherSlice,
  calcKcalByAttrId,
  decimalPlaces,
  roundMealValue,
} from "../../../helpers/mealsHelper";

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 38px;

  @media (max-width: 991px) {
    margin-top: 1rem;
    justify-content: space-between;
    & > button {
      width: 100%;
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const LeftColWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const RightColWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  padding-left: 25px;
  margin-top: 0;

  @media (max-width: 991px) {
    width: 100%;
    padding-left: 0;
    margin-top: 30px;
  }
`;

const FavoriteWrapper = styled.div`
  margin-top: 25px;
`;

const DailyTargetsWrapper = styled.div`
  margin-top: 20px;
`;

const ReasonForChangeWrapper = styled.div`
  margin-top: 25px;
`;

const StyledCheckboxLabel = styled.div`
  font-weight: 600;
  margin-left: 15px;
  cursor: pointer;
`;

const StyledCheckboxWrapper = styled.div`
  display: flex;
  margin-top: 30px;
  margin-bottom: 15px;
`;

export const SmartFavoriteMeal = (props) => {
  const {
    dailyTargets,
    nutritionalInfo,
    setNutritionalInfo,
    selectedItems,
    setSelectedItems,
    calories,
    setCalories,
    chartsHelper,
    setChartsHelper,
    isSuccess,
    isError,
    setIsSuccess,
    setIsError,
    saveFavoriteMeal,
    nutritionFields,
    fetchNutritionFields,
    setAboutMeal,
    aboutMeal,
    updateReasonFor,
    setUpdateReasonFor,
    resetStates,
    defaultItems,
    favoriteId,
    onSubmit,
    mealJsonData,
    existingReasonForChange,
    type,
    readOnly,
    setModalTitle,
    favoriteMeal,
    theme,
    studyId,
    fetchDailyTargets,
  } = props;

  const { t } = useTranslation();

  const [favoriteName, setFavoriteName] = useState("");
  const [favoriteNameIsValid, setFavoriteNameIsValid] = useState(false);
  const [favoriteNameError, setFavoriteNameError] = useState(null);

  const [confirm, setConfirm] = useState(false);
  const [confirmingItems, setConfirmingItems] = useState(false);

  const [tabActiveKey, setTabActiveKey] = useState("first");

  const btnSaveErrorMngmt = {
    isSuccess: isSuccess,
    setIsSuccess: setIsSuccess,
    isError: isError,
    setIsError: setIsError,
  };

  useEffect(() => {
    if (favoriteMeal) {
      setFavoriteName(favoriteMeal.name);
    }
  }, [favoriteMeal]);

  const handleConfirm = () => {
    if (!selectedItems || selectedItems.length === 0) {
      setIsError(true);
      sendNotification(
        t("errorText.dietDiary.addFavorite.noItems"),
        ToastType.ERROR
      );
      return;
    }

    const anyItemWithoutQty = selectedItems.some(
      (item) => !item.qty || parseFloat(item.qty) === 0
    );
    if (anyItemWithoutQty) {
      setIsError(true);
      sendNotification(
        t("errorText.dietDiary.addFavorite.noQuantities"),
        ToastType.ERROR
      );
      return;
    }

    if (confirmingItems) {
      submitHandler();
    } else {
      scrollModalToTop();
      setConfirmingItems(true);
    }
  };

  useEffect(() => {
    if (setModalTitle && favoriteMeal) {
      if (confirmingItems) {
        setModalTitle(t("contentText.dietDiary.addFavorite.confirm.title"));
      } else {
        if (favoriteId && readOnly) {
          setModalTitle(`${t("appText.view")} ${favoriteMeal.name}`);
        } else if (favoriteId && !readOnly) {
          setModalTitle(`${t("appText.edit")} ${favoriteMeal.name}`);
        } else {
          setModalTitle(
            `${t("appText.add")} ${dietDairyTypeToString(favoriteMeal.type)}`
          );
        }
      }
    }
  }, [confirmingItems, favoriteMeal, favoriteId, readOnly, setModalTitle, t]);

  useEffect(() => {
    fetchDailyTargets();
  }, [fetchDailyTargets]);

  const getNutrientById = useCallback(
    (searchAttrId) => {
      const info = nutritionalInfo.find(
        ({ attr_id }) => attr_id === searchAttrId
      );

      return info;
    },
    [nutritionalInfo]
  );

  const getNutrientUnitById = useCallback(
    (searchAttrId) => {
      const info = getNutrientById(searchAttrId);

      return info ? info.unit : "";
    },
    [getNutrientById]
  );

  const dailyTargetsArr = useMemo(() => {
    return dailyTargets
      ? {
          goalTitle: t("appText.dietDiary.dailyTargets"),
          goalsList: [
            {
              goalLabel: t("appText.dietDiary.protein"),
              goalLevel: GoalLevels.greaterOrEqual,
              goalColor: getMacronutrientColor(
                dailyTargetIds.PROTEIN_ATTR_ID,
                theme,
                studyId
              ),
              goalValue: `${roundMealValue(
                dailyTargets?.proteinPercentage ?? 0
              )}%`,
            },
            {
              goalLabel: t("appText.dietDiary.carbohydrates"),
              goalLevel: GoalLevels.less,
              goalColor: getMacronutrientColor(
                dailyTargetIds.CARBS_ATTR_ID,
                theme,
                studyId
              ),
              goalValue: `${roundMealValue(
                dailyTargets?.carbohydratesPercentage ?? 0
              )}%`,
            },
          ],
          goalMessage: t("contentText.dietDiary.targets.sugars"),
        }
      : null;
  }, [dailyTargets, theme, studyId, t]);

  const setupChartsHelper = useCallback(() => {
    const chartInfoVals = PIE_CHART_SERIES_ATTRS.map((searchAttrId) => {
      const nutrient = nutritionalInfo.find(
        ({ attr_id }) => attr_id === searchAttrId
      );
      const numericValue = nutrient?.numericValue ?? 0.0;
      const title = nutrient ? nutrient.chartTitle : "";
      const aboutMealLetter = nutrient ? nutrient.aboutMealLetter : "";
      const value = nutrient ? nutrient.value : "-";

      return {
        attr_id: searchAttrId,
        letter: title,
        aboutMealLetter: aboutMealLetter,
        numericValue: numericValue,
        value: value,
      };
    });

    const total = calories.numericValue || 1;

    const sugar = nutritionalInfo.find(
      ({ attr_id }) => attr_id === dailyTargetIds.SUGAR_ATTR_ID
    );
    const sugarTitle = sugar ? sugar.chartTitle : "";
    const sugarNumericValue = sugar?.numericValue ?? 0.0;

    const tmpVals = {};
    const chartVals = chartInfoVals.map((item) => {
      const frequency = roundMealValue(
        (calcKcalByAttrId(item.numericValue, item.attr_id) / total) * 100.0
      );

      tmpVals[item.aboutMealLetter] = {
        value: item.numericValue,
        percentage: frequency,
        unit: getNutrientUnitById(item.attr_id),
      };

      let subInfo = null;

      if (item.attr_id === dailyTargetIds.CARBS_ATTR_ID) {
        subInfo = `(${sugarTitle}: ${roundMealValue(sugarNumericValue).toFixed(
          decimalPlaces
        )}g)`;

        tmpVals[sugar?.aboutMealLetter] = {
          value: roundMealValue(sugarNumericValue),
          unit: getNutrientUnitById(dailyTargetIds.SUGAR_ATTR_ID),
        };
      }

      return {
        letter: item.letter,
        frequency: frequency,
        color: getMacronutrientColor(item.attr_id, theme, studyId),
        percentage: `${Number(frequency).toFixed(decimalPlaces)}%`,
        info: `${Number(frequency).toFixed(decimalPlaces)}% - ${item.value}`,
        subInfo: subInfo,
        isVisible: true,
      };
    });

    chartVals && chartVals.push(addOtherSlice(chartVals, theme, studyId, t));

    setChartsHelper({
      pieChartSeries: chartVals,
    });

    setAboutMeal(tmpVals);
  }, [
    nutritionalInfo,
    setChartsHelper,
    setAboutMeal,
    getNutrientUnitById,
    theme,
    studyId,
    calories.numericValue,
    t,
  ]);

  useEffect(() => {
    const tmpCalories = nutritionalInfo.find(
      ({ attr_id }) => attr_id === dailyTargetIds.CALORIES_ATTR_ID
    );
    if (tmpCalories) {
      setCalories({
        percentage: 0,
        value: tmpCalories.value,
        numericValue: tmpCalories.numericValue,
        unit: getNutrientUnitById(dailyTargetIds.CALORIES_ATTR_ID),
      });
    }

    setupChartsHelper();
  }, [nutritionalInfo, setCalories, setupChartsHelper, getNutrientUnitById]);

  useEffect(() => {
    if (favoriteName) {
      setFavoriteNameIsValid(
        favoriteName.replace(/\.|,/g, "").trim() !== "" &&
          favoriteName.length <= 512
      );
    }
  }, [favoriteName]);

  const validateFavoriteName = (name) => {
    setFavoriteNameError(null);
    if (name.replace(/\.|,/g, "").trim() === "") {
      setFavoriteNameError(t("errorText.dietDiary.addFavorite.empty.name"));
    }
    if (name.length > 512) {
      setFavoriteNameError(t("errorText.dietDiary.addFavorite.large.name"));
    }
  };

  const backHandler = () => {
    if (readOnly) {
      onSubmit();
    } else {
      scrollModalToTop();
      setConfirm(false);
      setConfirmingItems(false);
      setFavoriteNameIsValid(false);
    }
  };

  const submitHandler = useCallback(() => {
    const tmpAboutMeal = aboutMeal;

    tmpAboutMeal["Calories"] = {
      percentage: 0,
      value: calories.numericValue,
      unit: calories.unit,
    };

    const mealJson = {
      aboutMeal: tmpAboutMeal,
      items: selectedItems,
      type: type,
    };

    let formData = {
      mealJson: JSON.stringify(mealJson),
    };

    if (favoriteId) {
      formData.name = favoriteName;
      formData.favoriteMealId = favoriteId;
      formData.reasonForChange = "Participant edited favorite";
    } else {
      if (favoriteNameIsValid) {
        formData.name = favoriteName;
      } else {
        setFavoriteNameIsValid(false);
        setIsError(true);
        return;
      }
    }

    saveFavoriteMeal(formData, favoriteId, type)
      .then((message) => {
        sendNotification(message, ToastType.SUCCESS);
        setIsSuccess(true);
        resetStates();
        onSubmit();
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
        setIsError(true);
      });
  }, [
    aboutMeal,
    calories,
    selectedItems,
    type,
    favoriteId,
    saveFavoriteMeal,
    setIsError,
    favoriteNameIsValid,
    favoriteName,
    setIsSuccess,
    resetStates,
    onSubmit,
  ]);

  useEffect(() => {
    return () => {
      resetStates();
    };
  }, [resetStates]);

  useEffect(() => {
    if (mealJsonData) {
      const mealData = JSON.parse(mealJsonData);
      setAboutMeal(mealData?.aboutMeal);
      setSelectedItems(mealData?.items);
    }
  }, [mealJsonData, setAboutMeal, setSelectedItems]);

  useEffect(() => {
    if (existingReasonForChange) {
      setUpdateReasonFor(existingReasonForChange);
    }
  }, [existingReasonForChange, setUpdateReasonFor]);

  const handleKeyPressCheckbox = (e) => {
    if (e.key === "Enter") {
      setConfirm((prevState) => !prevState);
    }
  };

  const tableHeaders = [
    { label: t("appText.description") },
    { label: t("appText.quantity") },
  ];

  return (
    <Container data-testid="favorite-meal">
      <Row>
        <LeftColWrapper>
          <SmartFoodDrinkSelector
            defaultItems={defaultItems}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            readOnly={confirmingItems || readOnly}
            title={t("appText.dietDiary.addMeal.confirm.label")}
            inputTitle={
              type === DietDairyTypes.FOOD_DRINK
                ? t("appText.dietDiary.addMeal.search.label.foodAndDrink")
                : t("appText.dietDiary.addMeal.search.label")
            }
          />

          {dailyTargetsArr && (
            <DailyTargetsWrapper>
              <DailyTargets {...dailyTargetsArr} />
            </DailyTargetsWrapper>
          )}
        </LeftColWrapper>
        <RightColWrapper>
          {confirmingItems ? (
            <FrequentlyForgottenItems dietDiaryType={type} />
          ) : (
            <CustomTabs
              defaultActiveKey="first"
              onSelect={(eventKey) => {
                setTabActiveKey(eventKey);
              }}
            >
              <TabButton eventKey="first">
                {t("appText.dietDiary.addMeal.aboutMeal")}
              </TabButton>
              <TabButton eventKey="second">
                {t("appText.dietDiary.addMeal.nutritionDetails")}
              </TabButton>
              <CustomTabContent eventKey="first">
                {selectedItems.length > 0 && (
                  <SmartDietDiaryCharts
                    constants={dailyTargetIds}
                    getNutrientById={getNutrientById}
                    calories={calories}
                    pieChartSeries={chartsHelper.pieChartSeries.filter(
                      (item) => item.frequency > 0
                    )}
                    getMacronutrientColor={getMacronutrientColor}
                    studyId={studyId}
                    rerenderPieChart={tabActiveKey === "first"}
                  />
                )}
              </CustomTabContent>
              <CustomTabContent eventKey="second">
                <NutritionTable
                  nutritionalInfo={nutritionalInfo}
                  setNutritionalInfo={setNutritionalInfo}
                  nutritionFields={nutritionFields}
                  fetchNutritionFields={fetchNutritionFields}
                  selectedItems={selectedItems}
                  tableHeaders={tableHeaders}
                />
              </CustomTabContent>
            </CustomTabs>
          )}

          {favoriteMeal && !readOnly && !confirmingItems && (
            <FavoriteWrapper>
              <Favorite
                defaultIsFavorite={true}
                showCheckbox={false}
                onChangeFavoriteName={setFavoriteName}
                onBlur={(e) => validateFavoriteName(e.target.value)}
                hasError={!!favoriteNameError}
                errorMessage={favoriteNameError}
                defaultFavoriteName={favoriteId ? favoriteMeal.name : null}
              />
            </FavoriteWrapper>
          )}

          {favoriteId && readOnly && updateReasonFor && (
            <ReasonForChangeWrapper>
              <LabelledInput
                title={t("appText.reasonForChange")}
                type="text"
                data-testid="update-reason-input"
                value={updateReasonFor}
                disabled={true}
              />
            </ReasonForChangeWrapper>
          )}

          {confirmingItems && !readOnly && (
            <StyledCheckboxWrapper
              tabIndex="0"
              onKeyPress={handleKeyPressCheckbox}
            >
              <Checkbox
                data-testid="confirm-checkbox"
                checked={confirm}
                onClick={() => setConfirm((prevState) => !prevState)}
                icon={true}
                square={true}
              />
              <StyledCheckboxLabel
                data-testid="confirm-checkbox-label"
                onClick={() => setConfirm((prevState) => !prevState)}
              >
                {t("contentText.dietDiary.addMeal.confirm.label")}
              </StyledCheckboxLabel>
            </StyledCheckboxWrapper>
          )}
        </RightColWrapper>
      </Row>

      {favoriteMeal && (
        <ActionButtonsContainer>
          {confirmingItems && (
            <SecondaryButton
              data-testid="back-button"
              className="mr-3"
              onClick={() => backHandler()}
            >
              {t("appText.back")}
            </SecondaryButton>
          )}

          {readOnly && (
            <PrimaryButton
              data-testid="close-button"
              onClick={() => backHandler()}
            >
              {t("appText.close")}
            </PrimaryButton>
          )}

          {readOnly ? (
            <div></div>
          ) : confirmingItems ? (
            <AnimatedButton
              data-testid="submit-button"
              icon={null}
              onClick={() => handleConfirm()}
              disabled={!confirm}
              keepBorder={true}
              {...btnSaveErrorMngmt}
            >
              {t("appText.submit")}
            </AnimatedButton>
          ) : (
            <PrimaryButton
              data-testid="next-button"
              disabled={!favoriteNameIsValid}
              onClick={() => handleConfirm()}
            >
              {t("appText.next")}
            </PrimaryButton>
          )}
        </ActionButtonsContainer>
      )}
    </Container>
  );
};

export default dynamic(
  "favoriteMealReducer",
  subspaced({
    // We map extra state because we might need to acess other stores for data, subspace is self contained.
    mapExtraState: (state) => {
      return {
        loginReducer: state.loginReducer,
      };
    },
  }),
  attachReducer(favoriteMealReducer)
)(connect(mapStateToProps, mapDispatchToProps)(withTheme(SmartFavoriteMeal)));
